import {
  ChevronLeft,
  ChevronRight,
  CirclePause,
  CirclePlay,
  Database,
  Gamepad2,
  Layers3,
  ShieldCheck,
  Users,
  WifiOff,
} from "lucide-react";
import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { httpsCallable } from "firebase/functions";
import { functions } from "../lib/firebaseFunctions";
import "./Scribex.css";

const PAGE_TITLE = "Scribex — Founders Beta | Spellgather";
const PAGE_DESCRIPTION =
  "Meet Scribex, a purpose-built e-paper tabletop companion from Spellgather. Physical XTEINK X3 beta testing is underway. Join the Founders list for future availability.";
const CANONICAL_URL = "https://www.spellgather.com/scribex";
const SOCIAL_IMAGE_URL = "https://www.spellgather.com/assets/scribex/HOME.png";

const HERO_SLIDES = [
  {
    src: "/assets/scribex/HOME.png",
    alt: "Scribex life and game-state interface running on a physical XTEINK X3 device",
    label: "Game-state view",
    width: 1448,
    height: 1086,
  },
  {
    src: "/assets/scribex/ICON_SLOTS.png",
    alt: "Scribex configurable counter and icon-slot interface running on a physical XTEINK X3 device",
    label: "Configurable icon slots",
    width: 1586,
    height: 992,
  },
  {
    src: "/assets/scribex/KO.png",
    alt: "Scribex player elimination presentation running on a physical XTEINK X3 device",
    label: "Player elimination flow",
    width: 1586,
    height: 992,
  },
  {
    src: "/assets/scribex/MENU.png",
    alt: "Scribex tabletop utility menu running on a physical XTEINK X3 device",
    label: "Tabletop utility menu",
    width: 1584,
    height: 993,
  },
] as const;

const TABLE_FEATURES = [
  {
    icon: Users,
    title: "Built around the players",
    body: "Multiplayer layouts, player profiles, deck associations and commander-oriented workflows are being shaped around real table use.",
  },
  {
    icon: Gamepad2,
    title: "Focused game state",
    body: "Life, counters, icon slots, elimination presentation and end-of-game flows keep essential information close without turning the table into a phone screen.",
  },
  {
    icon: Database,
    title: "A record that persists",
    body: "Scrollstone game history, profile data and device settings are being tested as a durable record across play sessions.",
  },
  {
    icon: WifiOff,
    title: "Useful on its own",
    body: "The physical button-driven experience is designed to remain purposeful as a standalone tabletop tool while future connections are explored.",
  },
] as const;

const CURRENT_SYSTEMS = [
  "Tabletop life and game-state tracking",
  "Multiplayer layouts",
  "Player profiles and associated decks",
  "Scrollstone game-history records",
  "Commander-oriented workflows",
  "Configurable counters and icon slots",
  "KO and player-elimination presentation",
  "Winner and end-of-game flows",
  "Physical button-driven navigation",
  "Dice and tabletop utilities",
  "Persistent device settings",
  "Custom keyboard and search interfaces",
  "E-paper-specific interface design",
  "Custom Scribex artwork and animations",
  "Startup experience featuring Vic and the Scribex visual identity",
] as const;

const BETA_PRIORITIES = [
  "Stability",
  "Performance",
  "Faster interaction",
  "Navigation consistency",
  "Profile and deck workflows",
  "Game-state reliability",
  "Data persistence",
  "Visual refinement",
  "Hardware and recovery behavior",
] as const;

const FUTURE_DIRECTIONS = [
  {
    label: "Exploring",
    title: "Deeper Spellgather integration",
    body: "Possible connections between tabletop sessions, player identity, decks and the wider Spellgather experience.",
  },
  {
    label: "Planned for investigation",
    title: "Local multiplayer interaction",
    body: "Ways nearby devices might share selected table context while preserving clear player control.",
  },
  {
    label: "Possible future capability",
    title: "Companion connectivity",
    body: "Optional companion workflows, richer history and expanded utilities, subject to development and testing.",
  },
  {
    label: "Exploring",
    title: "A wider tabletop toolkit",
    body: "Additional game formats, community-driven improvements and future compatible hardware exploration.",
  },
] as const;

type SignupSource = "scribex-founders-panel" | "scribex-final-cta";

type SignupState =
  | { kind: "idle"; message: "" }
  | { kind: "submitting"; message: string }
  | { kind: "success"; message: string }
  | { kind: "already"; message: string }
  | { kind: "validation"; message: string }
  | { kind: "backend"; message: string };

interface FoundersInterestRequest {
  email: string;
  source: SignupSource;
  website: string;
}

interface FoundersInterestResponse {
  status: "created" | "already_registered";
}

interface FoundersSignupProps {
  formId: string;
  source: SignupSource;
  compact?: boolean;
}

function setMetaContent(
  selector: string,
  attributes: Record<string, string>,
  content: string,
): () => void {
  let element = document.head.querySelector<HTMLMetaElement>(selector);
  const created = element === null;
  const previousContent = element?.getAttribute("content") ?? null;

  if (!element) {
    element = document.createElement("meta");
    Object.entries(attributes).forEach(([name, value]) => element?.setAttribute(name, value));
    document.head.appendChild(element);
  }

  element.setAttribute("content", content);

  return () => {
    if (created) {
      element?.remove();
    } else if (previousContent === null) {
      element?.removeAttribute("content");
    } else {
      element?.setAttribute("content", previousContent);
    }
  };
}

function useScribexMetadata(): void {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = PAGE_TITLE;

    const restoreMeta = [
      setMetaContent('meta[name="description"]', { name: "description" }, PAGE_DESCRIPTION),
      setMetaContent('meta[property="og:type"]', { property: "og:type" }, "website"),
      setMetaContent('meta[property="og:site_name"]', { property: "og:site_name" }, "Spellgather"),
      setMetaContent('meta[property="og:title"]', { property: "og:title" }, PAGE_TITLE),
      setMetaContent('meta[property="og:description"]', { property: "og:description" }, PAGE_DESCRIPTION),
      setMetaContent('meta[property="og:url"]', { property: "og:url" }, CANONICAL_URL),
      setMetaContent('meta[property="og:image"]', { property: "og:image" }, SOCIAL_IMAGE_URL),
      setMetaContent(
        'meta[property="og:image:alt"]',
        { property: "og:image:alt" },
        "Scribex tabletop interface running on an XTEINK X3 e-paper device",
      ),
      setMetaContent('meta[name="twitter:card"]', { name: "twitter:card" }, "summary_large_image"),
      setMetaContent('meta[name="twitter:title"]', { name: "twitter:title" }, PAGE_TITLE),
      setMetaContent('meta[name="twitter:description"]', { name: "twitter:description" }, PAGE_DESCRIPTION),
      setMetaContent('meta[name="twitter:image"]', { name: "twitter:image" }, SOCIAL_IMAGE_URL),
    ];

    let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    const canonicalCreated = canonical === null;
    const previousCanonical = canonical?.getAttribute("href") ?? null;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", CANONICAL_URL);

    return () => {
      document.title = previousTitle;
      restoreMeta.forEach((restore) => restore());
      if (canonicalCreated) {
        canonical?.remove();
      } else if (previousCanonical === null) {
        canonical?.removeAttribute("href");
      } else {
        canonical?.setAttribute("href", previousCanonical);
      }
    };
  }, []);
}

function SectionHeading({
  eyebrow,
  title,
  children,
}: {
  eyebrow: string;
  title: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <div className="scribex-section-heading">
      <p className="scribex-eyebrow">{eyebrow}</p>
      <h2>{title}</h2>
      <div className="scribex-section-lede">{children}</div>
    </div>
  );
}

function FoundersSignup({ formId, source, compact = false }: FoundersSignupProps): JSX.Element {
  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState("");
  const [state, setState] = useState<SignupState>({ kind: "idle", message: "" });
  const statusId = `${formId}-status`;
  const detailId = `${formId}-details`;
  const isSubmitting = state.kind === "submitting";
  const isError = state.kind === "validation" || state.kind === "backend";

  const submit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    const normalizedEmail = email.trim().toLowerCase();
    const validEmail =
      normalizedEmail.length <= 254 && /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(normalizedEmail);

    if (!validEmail) {
      setState({ kind: "validation", message: "Enter a valid email address to join the list." });
      return;
    }

    if (website.trim()) {
      setState({ kind: "success", message: "Thanks — your interest has been registered." });
      return;
    }

    setState({ kind: "submitting", message: "Joining the Founders list…" });

    try {
      const registerInterest = httpsCallable<FoundersInterestRequest, FoundersInterestResponse>(
        functions,
        "registerScribexFoundersInterest",
      );
      const result = await registerInterest({ email: normalizedEmail, source, website });

      if (result.data.status === "already_registered") {
        setState({
          kind: "already",
          message: "You're already on the Scribex Founders list. We'll keep you posted.",
        });
        return;
      }

      setEmail(normalizedEmail);
      setState({
        kind: "success",
        message: "You're on the list. We'll email you when Founders details are ready.",
      });
    } catch {
      setState({
        kind: "backend",
        message: "We couldn't register your interest right now. Please try again in a moment.",
      });
    }
  };

  const updateEmail = (value: string): void => {
    setEmail(value);
    if (state.kind === "validation" || state.kind === "backend") {
      setState({ kind: "idle", message: "" });
    }
  };

  return (
    <form
      id={formId}
      className={compact ? "scribex-signup scribex-signup--compact" : "scribex-signup"}
      onSubmit={submit}
      noValidate
    >
      <div className="scribex-field">
        <label htmlFor={`${formId}-email`}>Email address</label>
        <input
          id={`${formId}-email`}
          type="email"
          value={email}
          onChange={(event) => updateEmail(event.target.value)}
          placeholder="you@example.com"
          autoComplete="email"
          inputMode="email"
          required
          disabled={isSubmitting}
          aria-invalid={isError}
          aria-describedby={`${detailId} ${statusId}`}
        />
      </div>
      <div className="scribex-honeypot" aria-hidden="true">
        <label htmlFor={`${formId}-website`}>Website</label>
        <input
          id={`${formId}-website`}
          type="text"
          value={website}
          onChange={(event) => setWebsite(event.target.value)}
          tabIndex={-1}
          autoComplete="off"
        />
      </div>
      <button className="scribex-button scribex-button--primary" type="submit" disabled={isSubmitting}>
        {isSubmitting ? "JOINING…" : "JOIN THE FOUNDERS LIST"}
      </button>
      <p id={detailId} className="scribex-signup-detail">
        By joining, you ask Spellgather to email you only about Scribex Founders availability and
        closely related package updates. Read the <Link to="/legal/privacy">privacy policy</Link>.
        Interest registration only. This is not a preorder, reservation or purchase commitment.
      </p>
      <p
        id={statusId}
        className={`scribex-signup-status scribex-signup-status--${state.kind}`}
        role={isError ? "alert" : "status"}
        aria-live={isError ? "assertive" : "polite"}
      >
        {state.message}
      </p>
    </form>
  );
}

function HeroGallery(): JSX.Element {
  const [index, setIndex] = useState(0);
  const [autoPlayEnabled, setAutoPlayEnabled] = useState(false);
  const [interactionPaused, setInteractionPaused] = useState(false);
  const slide = HERO_SLIDES[index];

  useEffect(() => {
    setAutoPlayEnabled(!window.matchMedia("(prefers-reduced-motion: reduce)").matches);
  }, []);

  useEffect(() => {
    if (!autoPlayEnabled || interactionPaused) return undefined;
    const timer = window.setInterval(() => {
      setIndex((current) => (current + 1) % HERO_SLIDES.length);
    }, 6500);
    return () => window.clearInterval(timer);
  }, [autoPlayEnabled, interactionPaused]);

  const previous = (): void => {
    setIndex((current) => (current - 1 + HERO_SLIDES.length) % HERO_SLIDES.length);
  };

  const next = (): void => {
    setIndex((current) => (current + 1) % HERO_SLIDES.length);
  };

  return (
    <div
      className="scribex-hero-gallery"
      role="region"
      aria-roledescription="carousel"
      aria-label="Scribex running on physical XTEINK X3 hardware"
      onMouseEnter={() => setInteractionPaused(true)}
      onMouseLeave={() => setInteractionPaused(false)}
      onFocus={() => setInteractionPaused(true)}
      onBlur={(event) => {
        if (!(event.relatedTarget instanceof Node) || !event.currentTarget.contains(event.relatedTarget)) {
          setInteractionPaused(false);
        }
      }}
      onKeyDown={(event) => {
        if (event.key === "ArrowLeft") {
          event.preventDefault();
          previous();
        }
        if (event.key === "ArrowRight") {
          event.preventDefault();
          next();
        }
      }}
    >
      <div className="scribex-photo-frame">
        <img
          key={slide.src}
          className="scribex-hero-photo"
          src={slide.src}
          alt={slide.alt}
          width={slide.width}
          height={slide.height}
          loading={index === 0 ? "eager" : "lazy"}
          fetchPriority={index === 0 ? "high" : "auto"}
          decoding="async"
        />
        <div className="scribex-photo-badge">
          <span className="scribex-live-dot" aria-hidden="true" />
          Physical beta hardware
        </div>
      </div>
      <div className="scribex-gallery-controls">
        <div>
          <span className="scribex-gallery-count">
            {String(index + 1).padStart(2, "0")} / {String(HERO_SLIDES.length).padStart(2, "0")}
          </span>
          <p aria-live="off">{slide.label}</p>
        </div>
        <div className="scribex-gallery-actions">
          <button
            type="button"
            className="scribex-icon-button"
            onClick={() => setAutoPlayEnabled((enabled) => !enabled)}
            aria-label={autoPlayEnabled ? "Pause product photos" : "Play product photos"}
          >
            {autoPlayEnabled ? <CirclePause aria-hidden="true" /> : <CirclePlay aria-hidden="true" />}
          </button>
          <button type="button" className="scribex-icon-button" onClick={previous} aria-label="Previous product photo">
            <ChevronLeft aria-hidden="true" />
          </button>
          <button type="button" className="scribex-icon-button" onClick={next} aria-label="Next product photo">
            <ChevronRight aria-hidden="true" />
          </button>
        </div>
      </div>
      <div className="scribex-gallery-dots" aria-label="Choose a product photo">
        {HERO_SLIDES.map((item, itemIndex) => (
          <button
            key={item.src}
            type="button"
            className={itemIndex === index ? "is-active" : ""}
            onClick={() => setIndex(itemIndex)}
            aria-label={`Show ${item.label.toLowerCase()}`}
            aria-current={itemIndex === index ? "true" : undefined}
          />
        ))}
      </div>
    </div>
  );
}

export default function Scribex(): JSX.Element {
  useScribexMetadata();

  useEffect(() => {
    document.body.classList.add("scribex-page");
    return () => document.body.classList.remove("scribex-page");
  }, []);

  return (
    <div className="scribex">
      <a className="scribex-skip-link" href="#scribex-content">
        Skip to Scribex content
      </a>

      <nav className="scribex-nav" aria-label="Scribex navigation">
        <div className="scribex-container scribex-nav-inner">
          <Link className="scribex-brand" to="/" aria-label="Spellgather home">
            <img src="/images/spellgather-logo.png" alt="" width="40" height="40" />
            <span>
              <strong>SCRIBEX</strong>
              <small>A Spellgather project</small>
            </span>
          </Link>
          <div className="scribex-nav-links">
            <a href="#experience">Experience</a>
            <a href="#beta">Beta status</a>
            <a href="#founders-version">Founders Version</a>
            <a href="#open-source">Credits</a>
          </div>
          <a className="scribex-button scribex-button--nav" href="#founders-list">
            Join the list
          </a>
        </div>
      </nav>

      <div id="scribex-content">
        <header className="scribex-hero">
          <div className="scribex-container scribex-hero-grid">
            <div className="scribex-hero-copy">
              <p className="scribex-eyebrow">SCRIBEX / FOUNDERS BETA</p>
              <h1>YOUR TABLE.<br />YOUR GAME.<br />YOUR RECORD.</h1>
              <p className="scribex-hero-subtitle">
                A purpose-built tabletop companion, now entering physical X3 beta testing.
              </p>
              <p className="scribex-hero-body">
                Scribex is a human-directed software experience from Spellgather, running on
                third-party XTEINK X3 e-paper hardware and built to stay focused on tabletop play.
                The limited Founders Beta interest list is now open.
              </p>
              <div className="scribex-hero-actions">
                <a className="scribex-button scribex-button--primary" href="#founders-list">
                  JOIN THE FOUNDERS LIST
                </a>
                <a className="scribex-button scribex-button--secondary" href="#about-scribex">
                  EXPLORE SCRIBEX
                </a>
              </div>
              <div className="scribex-status-line">
                <span className="scribex-live-dot" aria-hidden="true" />
                <strong>Now in physical hardware beta testing</strong>
              </div>
            </div>
            <HeroGallery />
          </div>
        </header>

        <section id="founders-list" className="scribex-signup-band" aria-labelledby="founders-list-title">
          <div className="scribex-container scribex-signup-panel">
            <div className="scribex-signup-copy">
              <p className="scribex-eyebrow">FOUNDERS INTEREST LIST</p>
              <h2 id="founders-list-title">BE THERE FOR THE FIRST RUN</h2>
              <p>
                We’re preparing a limited Founders Beta built around new XTEINK X3 hardware
                configured with Scribex. Join the list to hear when availability, package details
                and pricing are ready.
              </p>
            </div>
            <FoundersSignup formId="scribex-founders-primary" source="scribex-founders-panel" />
          </div>
        </section>

        <section id="about-scribex" className="scribex-section">
          <div className="scribex-container">
            <SectionHeading eyebrow="WHAT IS SCRIBEX?" title="SOFTWARE MADE FOR THE SPACE BETWEEN PLAYERS">
              <p>
                Scribex transforms a compact e-paper device into a focused tabletop companion:
                game state in view, useful records close at hand and interactions shaped for
                physical play.
              </p>
            </SectionHeading>
            <div className="scribex-manifesto-grid">
              <p className="scribex-big-statement">
                Less screen noise.<br />More table presence.
              </p>
              <div className="scribex-body-stack">
                <p>
                  It has progressed beyond a concept or interface mockup. Core Scribex interfaces
                  and gameplay systems are running on X3 hardware while performance, reliability,
                  workflows and final interaction details continue to be validated.
                </p>
                <p>
                  Scribex tracks and presents player-directed information. It is not a rules engine,
                  judge or automated authority over a game.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section id="beta" className="scribex-section scribex-section--photos">
          <div className="scribex-container">
            <SectionHeading eyebrow="CURRENT BETA" title="RUNNING ON THE DEVICE, TESTED AT THE TABLE">
              <p>
                Real XTEINK X3 units are being used to validate rendering, navigation, startup and
                representative tabletop workflows in the physical experience.
              </p>
            </SectionHeading>
            <div className="scribex-photo-grid">
              <figure className="scribex-product-shot scribex-product-shot--wide">
                <img
                  src="/assets/scribex/ICON_SLOTS.png"
                  alt="Configurable counter and icon slots displayed by Scribex on physical XTEINK X3 hardware"
                  width="1586"
                  height="992"
                  loading="lazy"
                  decoding="async"
                />
                <figcaption><span>01</span> Configurable game-state presentation</figcaption>
              </figure>
              <figure className="scribex-product-shot">
                <img
                  src="/assets/scribex/KO.png"
                  alt="Player elimination screen displayed by Scribex on physical XTEINK X3 hardware"
                  width="1586"
                  height="992"
                  loading="lazy"
                  decoding="async"
                />
                <figcaption><span>02</span> Deliberate winner and elimination flows</figcaption>
              </figure>
            </div>
          </div>
        </section>

        <section id="experience" className="scribex-section">
          <div className="scribex-container">
            <SectionHeading eyebrow="BUILT FOR THE TABLE" title="A COMPANION, NOT ANOTHER DISTRACTION">
              <p>
                The experience is being designed as a tactile part of the play space, with e-paper
                contrast, physical controls and workflows that keep attention on the people and the game.
              </p>
            </SectionHeading>
            <div className="scribex-feature-grid">
              {TABLE_FEATURES.map((feature) => {
                const Icon = feature.icon;
                return (
                  <article key={feature.title} className="scribex-feature-card">
                    <Icon aria-hidden="true" />
                    <h3>{feature.title}</h3>
                    <p>{feature.body}</p>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        <section className="scribex-section scribex-section--systems">
          <div className="scribex-container scribex-systems-layout">
            <SectionHeading eyebrow="CURRENTLY BEING BUILT + TESTED" title="THE SCRIBEX EXPERIENCE IS TAKING SHAPE">
              <p>
                These are systems currently implemented, being integrated or undergoing hardware
                validation. Inclusion here does not mean every part is feature-complete.
              </p>
            </SectionHeading>
            <ul className="scribex-system-list">
              {CURRENT_SYSTEMS.map((system, index) => (
                <li key={system}>
                  <span>{String(index + 1).padStart(2, "0")}</span>
                  {system}
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="scribex-section">
          <div className="scribex-container scribex-beta-panel">
            <div className="scribex-beta-callout">
              <p className="scribex-eyebrow">PHYSICAL BETA STATUS</p>
              <h2>SUBSTANTIAL PROGRESS. ACTIVE VALIDATION.</h2>
              <p>
                Physical testing has validated substantial portions of the startup, rendering,
                navigation and interface experience. The Founders build remains beta software.
                Current testing is focused on reliability, responsiveness, persistent game data,
                workflow consistency and final hardware behavior.
              </p>
            </div>
            <ul className="scribex-priority-list" aria-label="Current beta priorities">
              {BETA_PRIORITIES.map((priority) => <li key={priority}>{priority}</li>)}
            </ul>
          </div>
        </section>

        <section id="founders-version" className="scribex-section scribex-section--founders">
          <div className="scribex-container scribex-founders-grid">
            <div>
              <p className="scribex-eyebrow">SCRIBEX FOUNDERS VERSION</p>
              <p className="scribex-limited-mark">LIMITED BETA</p>
              <h2>A FIRST-RUN PACKAGE FOR EARLY SUPPORTERS</h2>
              <p className="scribex-founders-lede">
                The planned Founders Version is intended to pair a new XTEINK X3 hardware unit with
                the Scribex software experience installed and configured by Spellgather.
              </p>
            </div>
            <div className="scribex-package-card">
              <p className="scribex-package-label">CURRENT PACKAGE INTENT</p>
              <ul>
                <li>A new XTEINK X3 device</li>
                <li>The Scribex custom software installed and configured</li>
                <li>Standard/original XTEINK X3 package contents applicable to that unit</li>
                <li>Founders-edition Scribex extras currently being finalized</li>
                <li>Access to the early Founders/Beta release</li>
                <li>Founders identification or edition treatment, if later finalized</li>
              </ul>
              <div className="scribex-package-note">
                <ShieldCheck aria-hidden="true" />
                <p>
                  Configuration may evolve before release. Quantity, price and availability are not
                  finalized; hardware supply may affect the program. Joining the list expresses
                  interest only and does not guarantee a unit.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="scribex-section scribex-section--future">
          <div className="scribex-container">
            <SectionHeading eyebrow="WHERE SCRIBEX CAN GO" title="A DIRECTION, NOT A PROMISE">
              <p>
                The following areas are exploratory. They are possible future capabilities, subject
                to development, hardware constraints and testing, with no promised release dates.
              </p>
            </SectionHeading>
            <div className="scribex-future-grid">
              {FUTURE_DIRECTIONS.map((direction) => (
                <article key={direction.title}>
                  <span>{direction.label}</span>
                  <h3>{direction.title}</h3>
                  <p>{direction.body}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="open-source" className="scribex-section scribex-section--foundations">
          <div className="scribex-container">
            <SectionHeading eyebrow="BUILT ON OPEN FOUNDATIONS" title="SUPPORTED BY OPEN-SOURCE ENGINEERING">
              <p>
                Scribex does not start from a blank hardware abstraction layer. Its X3 firmware work
                builds upon open-source foundations from the CrossPoint and FreeInk communities,
                which provide important e-paper and hardware support.
              </p>
            </SectionHeading>
            <div className="scribex-foundation-statement">
              <Layers3 aria-hidden="true" />
              <p>
                Spellgather’s Scribex work builds its own tabletop-focused interface, artwork,
                workflows, game systems and product experience on top of those foundations. No
                affiliation, sponsorship or endorsement by those projects is implied.
              </p>
            </div>

            <div className="scribex-acknowledgements" aria-labelledby="acknowledgements-title">
              <div className="scribex-ack-heading">
                <p className="scribex-eyebrow">OPEN-SOURCE ACKNOWLEDGEMENTS</p>
                <h2 id="acknowledgements-title">PROJECTS THAT MAKE THE X3 WORK POSSIBLE</h2>
              </div>
              <article>
                <h3>CrossPoint Reader <span>MIT License</span></h3>
                <p>
                  Open-source e-reader firmware supporting devices including the XTEINK X3. Its
                  ecosystem serves as an engineering foundation for Scribex’s X3 work.
                </p>
                <div className="scribex-source-links">
                  <a href="https://github.com/crosspoint-reader/crosspoint-reader" target="_blank" rel="noreferrer">Upstream project</a>
                  <a href="https://github.com/crosspoint-reader/crosspoint-reader/blob/develop/LICENSE" target="_blank" rel="noreferrer">MIT License</a>
                </div>
              </article>
              <article>
                <h3>FreeInk SDK <span>MIT License</span></h3>
                <p>
                  The hardware-independent e-paper SDK used in the Scribex firmware architecture.
                  FreeInk maintains attribution for MIT-licensed work from the OpenX4 Community SDK
                  through its NOTICE file.
                </p>
                <div className="scribex-source-links">
                  <a href="https://github.com/Free-Ink/freeink-sdk" target="_blank" rel="noreferrer">Upstream project</a>
                  <a href="https://github.com/Free-Ink/freeink-sdk/blob/main/LICENSE" target="_blank" rel="noreferrer">MIT License</a>
                  <a href="https://github.com/Free-Ink/freeink-sdk/blob/main/NOTICE" target="_blank" rel="noreferrer">NOTICE</a>
                </div>
              </article>
              <details className="scribex-legal-detail">
                <summary>Other open-source lineage and license scope</summary>
                <div>
                  <p>
                    FreeInk’s NOTICE identifies derived portions from the MIT-licensed OpenX4
                    E-Paper Community SDK. See the <a href="https://github.com/open-x4-epaper/community-sdk" target="_blank" rel="noreferrer">OpenX4 Community SDK</a> and
                    FreeInk NOTICE for upstream attribution.
                  </p>
                  <p>
                    These dependency licenses apply to their respective components and do not
                    determine the license of Scribex as a whole. A consolidated notices page can be
                    added alongside distribution details without expanding this marketing section.
                  </p>
                </div>
              </details>
            </div>
          </div>
        </section>

        <section className="scribex-section scribex-section--legal" aria-labelledby="disclosures-title">
          <div className="scribex-container">
            <div className="scribex-legal-heading">
              <p className="scribex-eyebrow">DISCLOSURES + CREDITS</p>
              <h2 id="disclosures-title">CLEAR ABOUT WHAT SCRIBEX IS</h2>
            </div>
            <div className="scribex-details-grid">
              <details open>
                <summary>Hardware and third-party software</summary>
                <div>
                  <p>
                    Scribex is independently developed by Spellgather and is not affiliated with,
                    sponsored by, or endorsed by XTEINK. XTEINK and XTEINK X3 are referenced only to
                    identify the third-party hardware platform used by Scribex.
                  </p>
                  <p>
                    XTEINK manufactures the hardware. Spellgather develops and installs the Scribex
                    software experience; Scribex is third-party software and is not official XTEINK firmware.
                  </p>
                  <p>
                    Use of third-party software may affect support, updates, recovery procedures or
                    warranty treatment. Refer to the hardware manufacturer’s current policies for details.
                  </p>
                </div>
              </details>
              <details>
                <summary>Tabletop games and third-party trademarks</summary>
                <div>
                  <p>
                    Scribex supports generic tabletop and trading-card workflows. Third-party game
                    names or trademarks are used only where needed to describe compatibility or
                    identify a play format, and remain the property of their respective owners.
                  </p>
                  <p>
                    Magic: The Gathering and Wizards of the Coast are referenced only for
                    compatibility context. Scribex and Spellgather are not affiliated with,
                    sponsored by or endorsed by Wizards of the Coast or any other game publisher.
                  </p>
                </div>
              </details>
              <details>
                <summary>Creative ownership and attribution</summary>
                <div>
                  <p>
                    Spellgather’s Scribex-specific work includes the product concept, UI/product
                    design, custom visual geometry, workflows, written material and owned custom
                    assets. Custom artwork and design direction are attributed to Evictme.art where applicable.
                  </p>
                  <p>
                    Those claims do not extend to XTEINK hardware or trademarks, CrossPoint, FreeInk,
                    third-party open-source components or third-party game intellectual property.
                  </p>
                </div>
              </details>
              <details>
                <summary>Human direction and AI-assisted tools</summary>
                <div>
                  <p>
                    Scribex is a human-directed design and engineering project. AI-assisted tools are
                    used as part of its development workflow for tasks including engineering
                    assistance, iteration, research and selected artwork enhancement. Product
                    direction, visual design decisions, curation and final implementation remain human-directed.
                  </p>
                </div>
              </details>
            </div>
          </div>
        </section>

        <section className="scribex-final-cta" aria-labelledby="final-founders-title">
          <div className="scribex-container scribex-final-cta-inner">
            <div>
              <p className="scribex-eyebrow">INTERESTED IN A FOUNDERS VERSION?</p>
              <h2 id="final-founders-title">GET NOTIFIED WHEN FOUNDERS UNITS MAY BECOME AVAILABLE.</h2>
              <p>
                Availability is not guaranteed. Joining the list is simply the best way to hear when
                package details, pricing and timing are ready to share.
              </p>
            </div>
            <FoundersSignup formId="scribex-founders-final" source="scribex-final-cta" compact />
          </div>
        </section>
      </div>

      <footer className="scribex-footer">
        <div className="scribex-container scribex-footer-inner">
          <div className="scribex-footer-brand">
            <img src="/images/spellgather-logo.png" alt="" width="36" height="36" loading="lazy" />
            <p><strong>SCRIBEX</strong><span>A Spellgather project</span></p>
          </div>
          <p>Human-directed tabletop software. Physical beta in progress.</p>
          <div className="scribex-footer-links">
            <Link to="/legal/privacy">Privacy</Link>
            <a href="#open-source">Open source</a>
            <a href="mailto:support@spellgather.com">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
